import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { API_ENDPOINTS } from '../../../../config/apiConfig';
import { ApprovalIntegration } from '../../../../components/generic';
import {
  renderSplitViewBadgeOrBoolean,
  splitViewFieldColSpanClass,
  splitViewFieldValueContainerClass,
  splitViewTextareaValueClass,
} from '../../../../components/generic/splitViewFieldDisplay';
import { deviationConfig } from '../../deviationConfig';
import { useFlexibleList } from '../../../../hooks/useSWR';
import { normalizeFileName } from '../../../../components/ui/utils/fileNameEncoding';

const DeviationDetailsTab = ({ 
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  onRecordUpdate
}) => {
  const { theme } = useTheme();
  
  // Fetch Deviation actions
  const { data: deviationActions = [], isLoading: actionsLoading } = useFlexibleList(
    selectedRecord?.id ? `/deviation-action/by-deviation/${selectedRecord.id}` : null
  );

  // Fetch action types for Implementation Actions Status
  const { data: actionTypes = [] } = useFlexibleList(
    config?.api?.lookupEndpoints?.deviationActionTypes ? config.api.lookupEndpoints.deviationActionTypes : null
  );

  // Helper function to get action type name
  const getActionTypeName = (actionTypeId) => {
    if (!actionTypeId) return '—';
    const actionType = actionTypes.find(type => type.id === actionTypeId);
    return actionType?.name || '—';
  };

  // Sort actions by actionType.record_order
  const sortedActions = [...deviationActions].sort((a, b) => {
    const getActionTypeOrder = (action) => {
      if (action.actionType?.record_order !== null && action.actionType?.record_order !== undefined) {
        return action.actionType.record_order;
      }
      if (action.actionTypeId && actionTypes.length > 0) {
        const actionType = actionTypes.find(type => type.id === action.actionTypeId);
        if (actionType?.record_order !== null && actionType?.record_order !== undefined) {
          return actionType.record_order;
        }
      }
      return action.id || 0;
    };
    
    const orderA = getActionTypeOrder(a);
    const orderB = getActionTypeOrder(b);
    
    if (orderA !== orderB) {
      return (orderA || 999999) - (orderB || 999999);
    }
    return (a.id || 0) - (b.id || 0);
  });

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }}
    >
      <div className="grid grid-cols-1 gap-4">
        {/* Deviation Review and Approval Integration - Moved to top */}
        {deviationConfig.shouldShowApprovalInDetails() && (
          <div className="mb-6">
            <ApprovalIntegration 
              entity={selectedRecord}
              entityType="deviation"
              entityData={{
                deviationCode: selectedRecord.code,
                deviationTitle: selectedRecord.title,
                status: selectedRecord.status?.name,
                criticality: selectedRecord.criticality?.name,
                reportedBy:
                  selectedRecord.reportedByName ||
                  selectedRecord.reportedBy?.userDisplayName ||
                  selectedRecord.reportedBy?.name,
                reportedAt: selectedRecord.reportedAt,
                discoveredBy: selectedRecord.discoveredBy?.name,
                discoveredAt: selectedRecord.discoveredAt,
                description: selectedRecord.description,
                createdBy: selectedRecord.createdBy?.userDisplayName,
                createdAt: selectedRecord.createdAt
              }}
              onApprovalUpdate={(approval) => {
                console.log('Review and approval updated:', approval);
                // You can add additional logic here to refresh the deviation data
              }}
              customTitle="Deviation Review and Approval Status"
              customButtonText={deviationConfig.approval.approvalButtonText}
              showEntityDetails={deviationConfig.approval.showApprovalStatus}
              showApprovalActions={deviationConfig.approval.showApprovalActions}
              showApprovalHistory={deviationConfig.approval.showApprovalHistory}
              formMode={deviationConfig.approval.formMode || 'modal'}
              config={{
                defaultApprovalType: deviationConfig.approval.defaultApprovalType,
                allowMultipleApprovals: deviationConfig.approval.allowMultipleApprovals
              }}
            />
          </div>
        )}
        {config?.formSections?.map((section, idx) => {
          // Skip custom component sections - they're handled separately
          if (section.layout === 'custom' && section.customComponent) {
            // Handle Assessment of Criticality and Impact section (includes Impact Assessment)
            if (section.title === 'Assessment of Criticality and Impact') {
              // Get impact assessment fields
              const impactGroups = [
                { name: 'customerImpact', label: 'Notification of Customer', switchField: 'customerImpact', noteField: 'customerImpactNote' },
                { name: 'impactOnProduct', label: 'Impact on Product', switchField: 'impactOnProduct', noteField: 'impactOnProductNote' },
                { name: 'impactOnOtherBatches', label: 'Impact on Other Batches', switchField: 'impactOnOtherBatches', noteField: 'impactOnOtherBatchesNote' },
                { name: 'impactOnOtherProducts', label: 'Impact on Other Products', switchField: 'impactOnOtherProducts', noteField: 'impactOnOtherProductsNote' },
                { name: 'impactOnOtherSystems', label: 'Impact on Other Systems', switchField: 'impactOnOtherSystems', noteField: 'impactOnOtherSystemsNote' }
              ];
              
              // Filter to only active impacts
              const activeImpacts = impactGroups.filter(group => selectedRecord[group.switchField] === true);
              
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-2">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
                  </div>
                  
                  {/* Criticality Categorization field */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    {section.fields?.filter(fieldName => fieldName === 'criticalityId').map((fieldName) => {
                      const f = fields?.find(ff => ff.name === fieldName);
                      if (!f || f.type === 'hidden') return null;
                      
                      let rawVal = selectedRecord[fieldName];
                      const isEmpty = rawVal == null || rawVal === "";
                      
                      // Compute display value
                      let displayVal = rawVal;
                      const displayField = f.table && f.table.displayField;
                      if (displayField && selectedRecord[displayField] != null) {
                        displayVal = selectedRecord[displayField];
                      } else if (/Id$/.test(f.name)) {
                        const base = f.name.replace(/Id$/, '');
                        const rel = selectedRecord[base];
                        if (rel && typeof rel === 'object' && rel !== null && !Array.isArray(rel)) {
                          displayVal = rel.name || rel.title || '—';
                        }
                      }
                      
                      return (
                        <div key={fieldName} className="md:col-span-2">
                          <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                          <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                            {displayVal ? (
                              <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.primary, theme.text.inverse)}>
                                {String(displayVal)}
                              </span>
                            ) : (
                              <span className={cn(theme.text.muted)}>—</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Impact Assessment - Same style as Possible Causes (two-column grid) */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">⚠️</span>
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      Impact Assessment
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {impactGroups.map((impact) => {
                      const isActive = selectedRecord[impact.switchField] === true;
                      const noteValue = selectedRecord[impact.noteField];
                      
                      return (
                        <React.Fragment key={impact.name}>
                          {/* Impact Status - Left Column */}
                          <div>
                            <div className={cn("text-xs mb-1", theme.text.secondary)}>{impact.label}</div>
                            <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                              {isActive ? (
                                <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                  </svg>
                                  <span>Yes</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                                  </svg>
                                  <span>No</span>
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Impact Note - Right Column */}
                          <div>
                            <div className={cn("text-xs mb-1", theme.text.secondary)}>{impact.label} Note</div>
                            <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                              {noteValue ? (
                                <span className="whitespace-pre-wrap">{noteValue}</span>
                              ) : (
                                <span className={cn(theme.text.muted)}>—</span>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            }
            
            // Handle Corrective and Preventive Actions (CAPA) section
            if (section.title === 'Corrective and Preventive Actions (CAPA)') {
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} ({deviationActions.length})
                    </div>
                  </div>
                  {actionsLoading ? (
                    <div className={cn("text-center py-4", theme.text.muted)}>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : deviationActions.length > 0 ? (
                    <div className="space-y-3">
                      {deviationActions.map((action, index) => (
                        <div key={action.id || index} className={cn(
                          "p-4 rounded-lg border transition-colors duration-200",
                          theme.border.subtle, 
                          theme.colors.background,
                          "hover:border-opacity-60"
                        )}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className={cn("text-sm font-medium mb-2", theme.text.primary)}>
                                {action.description || `Action ${index + 1}`}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                  <span className="font-medium">Type:</span>
                                  <span>{action.actionType?.name || 'Not set'}</span>
                                </div>
                                {action.responsible && (
                                  <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                    <span className="font-medium">Responsible:</span>
                                    <span>{action.responsible}</span>
                                  </div>
                                )}
                                {action.dueDate && (
                                  <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                    <span className="font-medium">Due Date:</span>
                                    <span>{new Date(action.dueDate).toLocaleDateString()}</span>
                                  </div>
                                )}
                                {action.closeDate && (
                                  <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                    <span className="font-medium">Closed:</span>
                                    <span>{new Date(action.closeDate).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {action.closeDate && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Closed
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-4 text-sm", theme.text.muted)}>
                      No corrective/preventive actions recorded
                    </div>
                  )}
                </div>
              );
            }
            
            // Handle Implementation section
            if (section.title === 'Implementation') {
              const implementationField = fields?.find(f => f.name === 'implementation');
              const implementationCompletedAtField = fields?.find(f => f.name === 'implementationCompletedAt');
              
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-2">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
                  </div>
                  
                  {/* Implementation fields in grid layout like Risk Assessment */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    {implementationField && (
                      <div>
                        <div className={cn("text-xs mb-1", theme.text.secondary)}>{implementationField.label}</div>
                        <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                          {selectedRecord.implementation === true ? (
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                              <span>Yes</span>
                            </span>
                          ) : selectedRecord.implementation === false ? (
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                              </svg>
                              <span>No</span>
                            </span>
                          ) : (
                            <span className={cn("inline-flex items-center gap-1", theme.text.muted)}>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="2"/>
                              </svg>
                              <span>—</span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {implementationCompletedAtField && (
                      <div>
                        <div className={cn("text-xs mb-1", theme.text.secondary)}>{implementationCompletedAtField.label}</div>
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {selectedRecord.implementationCompletedAt 
                            ? new Date(selectedRecord.implementationCompletedAt).toLocaleDateString() 
                            : <span className={cn(theme.text.muted)}>—</span>}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Implementation Actions Status - Same style as CAPA */}
                  {sortedActions.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 mb-3 mt-4">
                        <span className="text-base">📊</span>
                        <div className={cn("text-sm font-semibold", theme.text.primary)}>
                          Implementation Actions Status ({sortedActions.length})
                        </div>
                      </div>
                      <div className="space-y-3">
                        {sortedActions.map((action, index) => (
                          <div key={action.id || index} className={cn(
                            "p-4 rounded-lg border transition-colors duration-200",
                            theme.border.subtle, 
                            theme.colors.background,
                            "hover:border-opacity-60"
                          )}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className={cn("text-sm font-medium mb-2", theme.text.primary)}>
                                  {getActionTypeName(action.actionTypeId) || `Implementation Status ${index + 1}`}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                  {action.implementationStatus && (
                                    <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                      <span className="font-medium">Status:</span>
                                      <span>{action.implementationStatus}</span>
                                    </div>
                                  )}
                                  {action.qaResponsible && (
                                    <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                      <span className="font-medium">QA Responsible:</span>
                                      <span>{action.qaResponsible}</span>
                                    </div>
                                  )}
                                  {action.qaDate && (
                                    <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                      <span className="font-medium">QA Date:</span>
                                      <span>{new Date(action.qaDate).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  {action.newExtension && (
                                    <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                      <span className="font-medium">New Extension:</span>
                                      <span>{new Date(action.newExtension).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  {action.newDueDate && (
                                    <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                      <span className="font-medium">New Due Date:</span>
                                      <span>{new Date(action.newDueDate).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  {action.newImplementationStatus && (
                                    <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                      <span className="font-medium">New Status:</span>
                                      <span>{action.newImplementationStatus}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            }
            
            // Handle File Attachments section
            if (section.title === 'File Attachments') {
              const activeFiles = selectedRecordFiles?.filter(file => !file.isDeleted) || [];
              if (!config?.features?.fileAttachments) {
                return null;
              }
              // Always show the section if file attachments are enabled, even if empty
              if (!activeFiles || activeFiles.length === 0) {
                return (
                  <div key={idx}>
                    <div className="flex items-center gap-2 mb-3">
                      {section.icon && (
                        <span className="text-base" aria-hidden="true">{section.icon}</span>
                      )}
                      <div className={cn("text-sm font-semibold", theme.text.primary)}>
                        {section.title} (0)
                      </div>
                    </div>
                    <div className={cn("text-sm", theme.text.secondary)}>
                      No file attachments
                    </div>
                  </div>
                );
              }
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} ({activeFiles.length})
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {activeFiles.map((file, index) => (
                      <div key={index} className={cn("flex items-center gap-3 p-3 rounded-lg border", theme.colors.background, theme.border.subtle)}>
                        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className={cn("text-sm font-medium truncate", theme.text.primary)}>
                            {normalizeFileName(file.fileName || '')}
                          </div>
                          <div className={cn("text-xs", theme.text.secondary)}>
                            {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.fileType || 'Unknown type'}
                          </div>
                        </div>
                        <a 
                          href={API_ENDPOINTS.fileAttachment.download(file.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn("text-xs px-3 py-1 rounded-md transition-colors", theme.colors.primary, theme.text.inverse, "hover:opacity-80")}
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            
            if (section.title === 'Reference Document' && section.customComponent) {
              const RefDoc = section.customComponent;
              return (
                <div key={idx}>
                  <RefDoc formData={selectedRecord} config={config} isEditMode={false} />
                </div>
              );
            }

            return null;
          }
          
          // Regular field sections
          return (
            <div key={idx}>
              <div className="flex items-center gap-2 mb-2">
                {section.icon && (
                  <span className="text-base" aria-hidden="true">{section.icon}</span>
                )}
                <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.fields?.map((fieldName) => {
                  const f = fields?.find(ff => ff.name === fieldName);
                  if (!f || f.type === 'hidden') return null;
                  
                  let rawVal = selectedRecord[fieldName];
                  const isEmpty = rawVal == null || rawVal === "";

                  // Special handling for rootCauseIds (array field)
                  if (fieldName === 'rootCauseIds') {
                    const rootCauses = selectedRecord.rootCauses || [];
                    return (
                      <div key={fieldName} className="md:col-span-2">
                        <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {rootCauses.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {rootCauses.map((cause, idx) => (
                                <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.primary, theme.text.inverse)}>
                                  {cause.name || cause}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className={cn(theme.text.muted)}>—</span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Compute display value for select/lookups and special types
                  let displayVal = rawVal;
                  let isBadge = false;
                  let isStatus = false;
                  let isBoolean = false;

                  // Special handling for closedById field - check closedBy directly
                  if (fieldName === 'closedById') {
                    const closedBy = selectedRecord?.closedBy;
                    
                    // If closedBy is already a string (from table transformation), use it directly
                    if (typeof closedBy === 'string' && closedBy.trim() !== '') {
                      displayVal = closedBy;
                    } else if (closedBy && typeof closedBy === 'object' && closedBy !== null && !Array.isArray(closedBy)) {
                      // If it's an object, extract the display name
                      if (closedBy.userDisplayName && closedBy.userDisplayName.trim() !== '') {
                        displayVal = closedBy.userDisplayName;
                      } else if (closedBy.userFirstName && closedBy.userLastName) {
                        const fullName = `${closedBy.userFirstName} ${closedBy.userLastName}`.trim();
                        if (fullName !== '') {
                          displayVal = fullName;
                        } else {
                          displayVal = closedBy.name || closedBy.userEmail || closedBy.email || '—';
                        }
                      } else if (closedBy.name && closedBy.name.trim() !== '') {
                        displayVal = closedBy.name;
                      } else if (closedBy.userEmail && closedBy.userEmail.trim() !== '') {
                        displayVal = closedBy.userEmail;
                      } else if (closedBy.email && closedBy.email.trim() !== '') {
                        displayVal = closedBy.email;
                      } else {
                        displayVal = '—';
                      }
                    } else {
                      displayVal = '—'; // Show dash if no user data
                    }
                  }
                  // Try table.displayField (e.g., status, criticality, closedBy)
                  else {
                    const displayField = f.table && f.table.displayField;
                    if (displayField && selectedRecord[displayField] != null) {
                      const displayFieldValue = selectedRecord[displayField];
                      // Handle user objects (e.g., closedBy, reportedBy, etc.)
                      if (typeof displayFieldValue === 'object' && displayFieldValue !== null && !Array.isArray(displayFieldValue)) {
                        // For user objects, try multiple property names (check userDisplayName first for users)
                        // Order matters: userDisplayName is the primary field for user display
                        if (displayFieldValue.userDisplayName) {
                          displayVal = displayFieldValue.userDisplayName;
                        } else if (displayFieldValue.userFirstName && displayFieldValue.userLastName) {
                          displayVal = `${displayFieldValue.userFirstName} ${displayFieldValue.userLastName}`.trim();
                        } else if (displayFieldValue.name) {
                          displayVal = displayFieldValue.name;
                        } else if (displayFieldValue.title) {
                          displayVal = displayFieldValue.title;
                        } else if (displayFieldValue.firstName && displayFieldValue.lastName) {
                          displayVal = `${displayFieldValue.firstName} ${displayFieldValue.lastName}`.trim();
                        } else if (displayFieldValue.userEmail) {
                          displayVal = displayFieldValue.userEmail;
                        } else if (displayFieldValue.email) {
                          displayVal = displayFieldValue.email;
                        } else {
                          displayVal = '—'; // Show dash if no user info found
                        }
                      } else {
                        displayVal = displayFieldValue;
                      }
                      isBadge = f.table && (f.table.cellRenderer === 'badge' || f.table.cellRenderer === 'statusBadge');
                      isStatus = f.table && f.table.cellRenderer === 'statusBadge';
                    } else if (/Id$/.test(f.name)) {
                      // Try related object common patterns
                      const base = f.name.replace(/Id$/, '');
                      const rel = selectedRecord[base];
                      if (rel && typeof rel === 'object' && rel !== null && !Array.isArray(rel)) {
                        // Handle user objects - check userDisplayName first
                        if (rel.userDisplayName) {
                          displayVal = rel.userDisplayName;
                        } else if (rel.userFirstName && rel.userLastName) {
                          displayVal = `${rel.userFirstName} ${rel.userLastName}`.trim();
                        } else {
                          displayVal = rel.name || 
                                     rel.title || 
                                     (rel.firstName && rel.lastName ? `${rel.firstName} ${rel.lastName}`.trim() : null) ||
                                     rel.userEmail ||
                                     rel.email ||
                                     rel.status || 
                                     rel.label || 
                                     null;
                        }
                        // If we still don't have a value, don't show the raw ID
                        if (displayVal === null || displayVal === '') {
                          displayVal = '—'; // Show dash instead of ID number or empty string
                        }
                        isBadge = true;
                      } else if (rawVal != null && rel == null) {
                        // If we have an ID but no relation object, show dash
                        displayVal = '—';
                      }
                    }
                  }

                  // Type-based rendering
                  if ((f.type === 'switch') || f.type === 'boolean') {
                    isBoolean = true;
                  } else if (f.type === 'date') {
                    try { 
                      if (!isEmpty) displayVal = new Date(rawVal).toLocaleDateString(); 
                    } catch {}
                  }

                  const isLongText =
                    f.type === 'textarea' || fieldName === 'description';

                  return (
                    <div
                      key={fieldName}
                      className={splitViewFieldColSpanClass(f, fieldName, section)}
                    >
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={splitViewFieldValueContainerClass(f, fieldName, theme)}>
                        {isLongText ? (
                          <span className={splitViewTextareaValueClass()}>
                            {String(selectedRecord[fieldName] ?? '—')}
                          </span>
                        ) : (
                          renderSplitViewBadgeOrBoolean({
                            field: f,
                            record: selectedRecord,
                            theme,
                            rawVal,
                            displayVal,
                            isBadge,
                            isStatus,
                            isBoolean,
                          }) ?? (
                            <span className={cn(isEmpty && theme.text.muted)}>
                              {String(displayVal ?? '—')}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeviationDetailsTab;

